// src/components/gestionEvento/RegistroFrom/StepAtleta.js

import React from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Typography,
  TextField,
  Button,
  Box,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import PruebasFieldArray from './PruebasFieldArray';
import ContactoEmergenciaFieldArray from './ContactoEmergenciaFieldArray';
import { computeAge } from '../../../utils/dateUtils';
import DateInput from './DateInput';
import TeamSelector from '../Ajustes/Competidores/TeamSelector';

const StepAtleta = ({
  formik,
  handleBack,
  handleNext,
  equiposDisponibles,
  categoriasDisponibles,
  pruebasFiltradas,
  maxPruebas,
  registroFields,
  registroFieldLabels,
  habilitarMarcasSiembra,
  handleFileChange,
  maxRelevos,
  competencia,
}) => {
  return (
    <Grid container spacing={2}>
      {/* Cuenta Id */}
      {registroFields.cuentaId && (
        <Grid item xs={12} sm={6}>
          <Typography sx={{ marginBottom: 1 }}>
            {registroFieldLabels.cuentaId || 'Cuenta Id'}
          </Typography>
          <TextField
            label={registroFieldLabels.cuentaId || 'Cuenta Id'}
            name="cuentaId"
            value={formik.values.cuentaId || ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            fullWidth
            size="small"
          />
        </Grid>
      )}
      {/* Correo */}
      <Grid item xs={12} sm={6}>
        <Typography sx={{ marginBottom: 1 }}>
          {registroFieldLabels.email || 'Correo'}
        </Typography>
        <TextField
          label={registroFieldLabels.email || 'Correo'}
          name="email"
          type="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
          required
          fullWidth
          size="small"
        />
      </Grid>
      {/* Nombre y Apellido */}
      <Grid item xs={12} sm={6}>
        <Typography sx={{ marginBottom: 1 }}>
          {registroFieldLabels.nombre || 'Nombre(s)'}
        </Typography>
        <TextField
          label={registroFieldLabels.nombre || 'Nombre(s)'}
          name="nombre"
          value={formik.values.nombre}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.nombre && Boolean(formik.errors.nombre)}
          helperText={formik.touched.nombre && formik.errors.nombre}
          required
          fullWidth
          size="small"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography sx={{ marginBottom: 1 }}>
          {registroFieldLabels.apellido || 'Apellido(s)'}
        </Typography>
        <TextField
          label={registroFieldLabels.apellido || 'Apellido(s)'}
          name="apellido"
          value={formik.values.apellido}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.apellido && Boolean(formik.errors.apellido)}
          helperText={formik.touched.apellido && formik.errors.apellido}
          required
          fullWidth
          size="small"
        />
      </Grid>
      {/* Foto de Perfil */}
      {registroFields.fotoPerfil && (
        <Grid item xs={12} sm={6}>
          <Button variant="outlined" component="label" fullWidth size="small">
            {registroFieldLabels.fotoPerfil || 'Subir Foto de Perfil'}
            <input
              type="file"
              name="fotoPerfil"
              hidden
              onChange={(e) => handleFileChange(e, 'fotoPerfil')}
            />
          </Button>
          {formik.values.fotoPerfil && (
            <Box mt={1}>
              <img
                src={formik.values.fotoPerfil}
                alt="Foto de Perfil"
                style={{ maxWidth: 150, maxHeight: 150 }}
              />
            </Box>
          )}
        </Grid>
      )}
      {/* Rama */}
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle1">
          {registroFieldLabels.rama || 'Rama'}
        </Typography>
        <FormControl component="fieldset">
          <RadioGroup row name="rama" value={formik.values.rama} onChange={formik.handleChange}>
            <FormControlLabel value="Varonil" control={<Radio size="small" />} label="Varonil" />
            <FormControlLabel value="Femenil" control={<Radio size="small" />} label="Femenil" />
          </RadioGroup>
        </FormControl>
        {formik.touched.rama && formik.errors.rama && (
          <Typography variant="caption" color="error">
            {formik.errors.rama}
          </Typography>
        )}
      </Grid>
      {/* Fecha de Nacimiento */}
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle1">
          {registroFieldLabels.fechaNacimiento || 'Fecha de Nacimiento'}
        </Typography>
        <DateInput
          nameDay="diaNacimiento"
          nameMonth="mesNacimiento"
          nameYear="anioNacimiento"
          values={formik.values}
          handleChange={formik.handleChange}
          handleBlur={formik.handleBlur}
          errors={formik.errors}
          touched={formik.touched}
        />
      </Grid>
      {/* Selección de Categoría */}
      <Grid item xs={12} sm={6}>
        <Typography sx={{ marginBottom: 1 }}>
          {registroFieldLabels.categoria || 'Categoría'}
        </Typography>
        <Autocomplete
          freeSolo={false}
          openOnFocus
          options={categoriasDisponibles}
          getOptionLabel={(option) => option.nombre}
          value={
            categoriasDisponibles.find((cat) => cat.id === formik.values.categoriaId) || null
          }
          onChange={(event, newValue) => {
            if (!newValue) {
              formik.setFieldValue('categoriaId', '');
              formik.setFieldValue('categoria', '');
            } else {
              formik.setFieldValue('categoriaId', newValue.id);
              formik.setFieldValue('categoria', newValue.nombre);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              name="categoriaId"
              label={registroFieldLabels.categoria || 'Categoría'}
              onBlur={formik.handleBlur}
              error={formik.touched.categoriaId && Boolean(formik.errors.categoriaId)}
              helperText={formik.touched.categoriaId && formik.errors.categoriaId}
              required
              size="small"
            />
          )}
        />
        {
          (() => {
            const age = computeAge(
              formik.values.anioNacimiento,
              formik.values.mesNacimiento,
              formik.values.diaNacimiento
            );
            const sel = categoriasDisponibles.find((c) => c.id === formik.values.categoriaId);
            if (!age || !sel || typeof sel.edadMin !== 'number' || typeof sel.edadMax !== 'number') return null;
            const outOfRange = age < sel.edadMin || age > sel.edadMax;
            return outOfRange ? (
              <Typography variant="caption" sx={{ mt: 0.5 }} color="warning.main">
                Esta categoría no está en tu rango de edad. Tómalo en cuenta antes de continuar.
              </Typography>
            ) : null;
          })()
        }
      </Grid>
      {/* Asociación */}
      {registroFields.asociacion && (
        <Grid item xs={12} sm={6}>
          <Typography sx={{ marginBottom: 1 }}>
            {registroFieldLabels.asociacion || 'Asociación'}
          </Typography>
          <TextField
            label={registroFieldLabels.asociacion || 'Asociación'}
            name="asociacion"
            value={formik.values.asociacion || ''}
            onChange={formik.handleChange}
            fullWidth
            size="small"
          />
        </Grid>
      )}
      {/* Equipo */}
      <Grid item xs={12} sm={6}>
        <TeamSelector
          value={formik.values.equipo}
          onChange={(team) => formik.setFieldValue('equipo', team)}
        />
      </Grid>
      {/* Logo de Equipo */}
      {registroFields.logoEquipo && (
        <Grid item xs={12} sm={6}>
          <Button variant="outlined" component="label" fullWidth size="small">
            {registroFieldLabels.logoEquipo || 'Subir Logo del Equipo'}
            <input
              type="file"
              name="logoEquipo"
              hidden
              onChange={(e) => handleFileChange(e, 'logoEquipo')}
            />
          </Button>
          {formik.values.logoEquipo && (
            <Box mt={1}>
              <img
                src={formik.values.logoEquipo}
                alt="Logo del Equipo"
                style={{ maxWidth: 150, maxHeight: 150 }}
              />
            </Box>
          )}
        </Grid>
      )}
      {/* Pruebas */}
      <Grid item xs={12}>
        <Typography variant="h6">Selecciona tus pruebas</Typography>
      </Grid>
      <PruebasFieldArray
        values={formik.values}
        errors={formik.errors}
        touched={formik.touched}
        setFieldValue={formik.setFieldValue}
        handleChange={formik.handleChange}
        pruebasFiltradas={pruebasFiltradas}
        maxPruebas={maxPruebas}
        habilitarMarcasSiembra={habilitarMarcasSiembra}
        maxRelevos={maxRelevos}
        opcionesRelevos={
          competencia?.opcionesRelevos || {
            maxMiembros: 0,
            numeroSuplentes: 0,
            permitirSuplentes: false,
            permitirEquiposMixtos: false,
          }
        }
      />
      {/* Dirección */}
      {registroFields.direccion ? (
        <>
          <Grid item xs={12}>
            <Typography variant="h6">Dirección</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography sx={{ marginBottom: 1 }}>Dirección</Typography>
            <TextField
              label="Dirección"
              name="direccion"
              value={formik.values.direccion}
              onChange={formik.handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography sx={{ marginBottom: 1 }}>Ciudad</Typography>
            <TextField
              label="Ciudad"
              name="ciudad"
              value={formik.values.ciudad || ''}
              onChange={formik.handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography sx={{ marginBottom: 1 }}>Estado</Typography>
            <TextField
              label="Estado"
              name="estado"
              value={formik.values.estado}
              onChange={formik.handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography sx={{ marginBottom: 1 }}>País</Typography>
            <TextField
              label="País"
              name="pais"
              value={formik.values.pais}
              onChange={formik.handleChange}
              fullWidth
              size="small"
            />
          </Grid>
        </>
      ) : (
        <>
          {registroFields.ciudad && (
            <Grid item xs={12} sm={6}>
              <Typography sx={{ marginBottom: 1 }}>Ciudad</Typography>
              <TextField
                label="Ciudad"
                name="ciudad"
                value={formik.values.ciudad || ''}
                onChange={formik.handleChange}
                fullWidth
                size="small"
              />
            </Grid>
          )}
          {registroFields.estado && (
            <Grid item xs={12} sm={6}>
              <Typography sx={{ marginBottom: 1 }}>Estado</Typography>
              <TextField
                label="Estado"
                name="estado"
                value={formik.values.estado}
                onChange={formik.handleChange}
                fullWidth
                size="small"
              />
            </Grid>
          )}
          {registroFields.pais && (
            <Grid item xs={12} sm={6}>
              <Typography sx={{ marginBottom: 1 }}>País</Typography>
              <TextField
                label="País"
                name="pais"
                value={formik.values.pais}
                onChange={formik.handleChange}
                fullWidth
                size="small"
              />
            </Grid>
          )}
        </>
      )}
      {/* Contacto de Emergencia */}
      {registroFields.contactoEmergencia && (
        <>
          <Grid item xs={12}>
            <Typography variant="h6">Contacto de Emergencia</Typography>
          </Grid>
          <ContactoEmergenciaFieldArray
            values={formik.values}
            handleChange={formik.handleChange}
          />
        </>
      )}
      {/* Tutor para Menor de Edad */}
      {registroFields.menorEdad &&
        formik.values.anioNacimiento &&
        computeAge(
          formik.values.anioNacimiento,
          formik.values.mesNacimiento,
          formik.values.diaNacimiento
        ) < 18 && (
          <>
            <Grid item xs={12}>
              <Typography variant="h6">Información del Tutor(a)</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={registroFieldLabels.tutorNombre || 'Nombre del Tutor(a)'}
                name="tutorNombre"
                value={formik.values.tutorNombre}
                onChange={formik.handleChange}
                fullWidth
                size="small"
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={registroFieldLabels.tutorEmail || 'Email del Tutor(a)'}
                name="tutorEmail"
                type="email"
                value={formik.values.tutorEmail}
                onChange={formik.handleChange}
                fullWidth
                size="small"
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={registroFieldLabels.tutorTelefono || 'Teléfono del Tutor(a)'}
                name="tutorTelefono"
                value={formik.values.tutorTelefono}
                onChange={formik.handleChange}
                fullWidth
                size="small"
                required
              />
            </Grid>
          </>
        )}
      <Grid item xs={12}>
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button onClick={handleBack} sx={{ mr: 1 }}>
            Anterior
          </Button>
          <Button onClick={handleNext} variant="contained" color="primary">
            Siguiente
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
};

StepAtleta.propTypes = {
  formik: PropTypes.object.isRequired,
  equiposDisponibles: PropTypes.array.isRequired,
  categoriasDisponibles: PropTypes.array.isRequired,
  pruebasFiltradas: PropTypes.array.isRequired,
  maxPruebas: PropTypes.number.isRequired,
  registroFields: PropTypes.object.isRequired,
  registroFieldLabels: PropTypes.object.isRequired,
  habilitarMarcasSiembra: PropTypes.bool.isRequired,
  handleFileChange: PropTypes.func.isRequired,
  maxRelevos: PropTypes.number.isRequired,
  competencia: PropTypes.object,
  handleBack: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired,
};

export default StepAtleta;
